import axios from 'axios';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import {
    decodeImgUploadResponse,
    HandleUploadImgResponse,
    ImgUploadResponseType,
    RequestImgBody,
    toBase64,
} from 'src/AppState/FilesState/FilesState';
import {
    blobToFile,
    getDimensionFromBase64Image,
    getShortName,
    ImageSizeType,
    urlToFile,
} from 'src/domains/casino/utils/utils';
import Compressor from 'compressorjs';

type uploadStatus = 'upload_success' | 'upload_error' | 'upload_omitted';
export class GameImageUploaderState {
    @observable imagePreview: string = this.gameImage;
    @observable fileContent: File | null = null;
    @observable imageSize: ImageSizeType | null = null;
    @observable fileName: string = this.fileContent !== null ? this.fileContent.name : getShortName(this.gameImage);

    constructor(public readonly gameImage: string) {
        makeAutoObservable(this);
    }

    @computed get gameImageSize(): ImageSizeType | null {
        return this.imageSize;
    }

    @action readImageSize = async (): Promise<void> => {
        if (this.imagePreview !== '') {
            if (this.imagePreview.includes('s3.amazonaws')) {
                return;
            }
            if (this.imagePreview.includes('fullforce.prerelease-env.biz')) {
                return;
            }
            const file = await urlToFile(this.imagePreview, this.fileName);
            const size = await getDimensionFromBase64Image(file);
            this.imageSize = size;
            this.fileContent = file;
        }
    };

    @action handleUploadImage = async (event: React.SyntheticEvent<HTMLInputElement, Event>): Promise<void> => {
        if (event.currentTarget.files !== null) {
            if (event.currentTarget.files[0] !== undefined) {
                const image = URL.createObjectURL(event.currentTarget.files[0]);
                if (image !== undefined) {
                    this.imagePreview = image;
                }
                const file = event.currentTarget.files[0];
                if (file !== undefined) {
                    this.fileContent = file;
                }
            }
        }
        await this.readImageSize();
    };

    @action handleImageOptimise = async (): Promise<void> => {
        if (this.fileContent !== null) {
            new Compressor(this.fileContent, {
                quality: 0.9,
                maxHeight: 500,
                maxWidth: 500,
                success: async (file): Promise<void> => {
                    this.fileContent = blobToFile(file, this.fileName);
                    const image64 = await toBase64(this.fileContent);
                    if (image64 !== null) {
                        this.imagePreview = image64.toString();
                    }
                    await this.readImageSize();
                },
                error(error) {
                    console.log(error);
                },
            });
        }
    };

    @action handleRemoveImage = async (): Promise<void> => {
        this.imagePreview = '';
    };

    @action uploadImage = async (collectionSlug: string): Promise<uploadStatus> => {
        if (this.fileContent !== null) {
            const uploadResponse = await this.handleUploadAndSaveGameImage(this.fileContent, collectionSlug);
            if (uploadResponse.status === 'success') {
                if (uploadResponse.data.img_urls[0] !== undefined) {
                    this.imagePreview = uploadResponse.data.img_urls[0];
                    return 'upload_success';
                }
            }
            return 'upload_error';
        }
        this.imagePreview = this.gameImage;
        return 'upload_omitted';
    };

    private handleUploadAndSaveGameImage = async (image: File, directory: string): Promise<HandleUploadImgResponse> => {
        const image64 = await toBase64(image);
        if (image64 !== null) {
            const base64String = image64.toString();
            const mimeTypeStart = base64String.indexOf(':') + 1;
            const formatStart = base64String.indexOf('/') + 1;
            const endData = base64String.indexOf(';');
            const base64Start = base64String.indexOf(',') + 1;
            const imgType = base64String.slice(formatStart, endData);
            const mimeType = base64String.slice(mimeTypeStart, endData);
            const base64 = base64String.slice(base64Start);

            const body = {
                base64,
                img_type: imgType,
                mime_type: mimeType,
                directory: directory,
            };

            try {
                const response = await this.uploadImageRequest(body, directory);

                if (response === null) {
                    return {
                        status: 'error',
                    };
                }

                return {
                    status: 'success',
                    data: response,
                };
            } catch (error) {
                return {
                    status: 'error',
                };
            }
        }
        return {
            status: 'error',
        };
    };

    private uploadImageRequest = async (
        baseData: RequestImgBody,
        directory: string
    ): Promise<ImgUploadResponseType | null> => {
        const response = await axios.request({
            method: 'POST',
            url: '/api-web/custom_request/post-upload-img',
            data: {
                baseData,
                directory: directory,
            },
        });

        const decodedResponse = decodeImgUploadResponse(response.data);
        if (decodedResponse instanceof Error) {
            console.error(decodedResponse);
            return null;
        }
        return decodedResponse;
    };
}
